import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, Plus, X, User } from "lucide-react";
import { Contacts } from "@/api/endpoints";
import type { ContactCreate, ContactKind, ContactAffiliation } from "@/api/types";

interface ContactFormProps {
  onSubmit: (payload: {
    contact_id?: string;
    contact?: ContactCreate;
    relationship?: string | null;
  }) => void;
  onCancel: () => void;
  submitting?: boolean;
}

type Mode = "link" | "create";

const KIND_OPTIONS: { value: ContactKind; label: string }[] = [
  { value: "account_contact", label: "Account contact" },
  { value: "broker_contact", label: "Broker" },
];

const AFFILIATION_OPTIONS: { value: ContactAffiliation; label: string }[] = [
  { value: "external", label: "External" },
  { value: "internal", label: "Internal" },
];

const isTechmellonEmail = (email: string) =>
  email.trim().toLowerCase().endsWith("@techmellon.com");

export default function ContactForm({ onSubmit, onCancel, submitting }: ContactFormProps) {
  const [mode, setMode] = useState<Mode>("link");
  const [relationship, setRelationship] = useState("");

  // Link-existing state
  const [q, setQ] = useState("");

  // Create-new state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<ContactKind>("account_contact");
  const [affiliation, setAffiliation] = useState<ContactAffiliation>("external");
  const [affiliationTouched, setAffiliationTouched] = useState(false);
  const [notes, setNotes] = useState("");

  const searchQuery = useQuery({
    queryKey: ["contacts", q],
    queryFn: () => Contacts.list({ q }),
    enabled: q.trim().length >= 2,
  });

  const rel = () => {
    const trimmed = relationship.trim();
    return trimmed ? trimmed : null;
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
    // Auto-suggest internal for techmellon.com addresses, unless the user has
    // manually overridden the affiliation already.
    if (!affiliationTouched && isTechmellonEmail(value)) {
      setAffiliation("internal");
    }
  };

  const selectExisting = (id: string) => {
    onSubmit({ contact_id: id, relationship: rel() });
  };

  const submitNew = () => {
    if (!name.trim()) return;
    const contact: ContactCreate = {
      name: name.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      title: title.trim() || null,
      kind,
      affiliation,
      notes: notes.trim() || null,
    };
    onSubmit({ contact, relationship: rel() });
  };

  const results = searchQuery.data ?? [];

  return (
    <div className="panel-tight p-3 space-y-3">
      {/* Mode toggle + cancel */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          <button
            type="button"
            className={`btn ${mode === "link" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setMode("link")}
          >
            <Search className="w-3.5 h-3.5" /> Link existing
          </button>
          <button
            type="button"
            className={`btn ${mode === "create" ? "btn-primary" : "btn-ghost"}`}
            onClick={() => setMode("create")}
          >
            <Plus className="w-3.5 h-3.5" /> Create new
          </button>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          aria-label="Cancel"
          title="Cancel"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Relationship — applies to both modes */}
      <div>
        <label className="label">Relationship (optional)</label>
        <input
          className="input"
          placeholder="e.g. Decision maker, Champion…"
          value={relationship}
          onChange={(e) => setRelationship(e.target.value)}
        />
      </div>

      {mode === "link" ? (
        <div className="space-y-2">
          <div>
            <label className="label">Search contacts</label>
            <input
              className="input"
              placeholder="Type at least 2 characters…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search contacts"
            />
          </div>
          {q.trim().length < 2 ? (
            <div className="text-sm text-mut-500 italic">
              Type to search existing contacts.
            </div>
          ) : searchQuery.isLoading ? (
            <div className="text-sm text-mut-300">Searching…</div>
          ) : results.length === 0 ? (
            <div className="text-sm text-mut-300 italic">No matches.</div>
          ) : (
            <ul className="space-y-1 max-h-56 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full text-left panel-tight p-2 flex items-start gap-2 disabled:opacity-50"
                    onClick={() => selectExisting(c.id)}
                    disabled={submitting}
                  >
                    <User className="w-3.5 h-3.5 mt-0.5 shrink-0 text-mut-300" />
                    <span className="min-w-0">
                      <span className="block text-md truncate">
                        {c.name}
                        {c.title && <span className="text-mut-300"> · {c.title}</span>}
                      </span>
                      {c.email && (
                        <span className="block text-xs text-mut-500 truncate">{c.email}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Contact name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className="input"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="label">Title</label>
            <input
              className="input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Kind</label>
              <select
                className="input"
                value={kind}
                onChange={(e) => setKind(e.target.value as ContactKind)}
              >
                {KIND_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Affiliation</label>
              <select
                className="input"
                value={affiliation}
                onChange={(e) => {
                  setAffiliationTouched(true);
                  setAffiliation(e.target.value as ContactAffiliation);
                }}
              >
                {AFFILIATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              className="input min-h-[60px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={submitNew}
              disabled={!name.trim() || submitting}
            >
              <Plus className="w-3.5 h-3.5" /> Add contact
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
